import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';
import { vacancyService } from '../../services/vacancyService';
import { courseService } from '../../services/courseService';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import EmployeeNav from '../../components/EmployeeNav';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';

const EmployeeHome = () => {
    const navigate = useNavigate();
    const user = authService.getCurrentUser();
    const { t, locale } = useTranslation();
    const [searchQuery, setSearchQuery] = useState('');
    const [recommendedJobs, setRecommendedJobs] = useState([]);
    const [allJobs, setAllJobs] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [coursesLoading, setCoursesLoading] = useState(true);

    const fetchJobs = async () => {
        try {
            setLoading(true);
            const response = await vacancyService.getAll({ limit: 10 });

            // API returns: { success: true, data: [vacancies] }
            let jobs = [];
            if (response && response.success && response.data) {
                jobs = Array.isArray(response.data) ? response.data : [];
            } else if (Array.isArray(response)) {
                // Fallback: if response is directly an array
                jobs = response;
            }

            setAllJobs(jobs);
            // For now, use first 3 jobs as recommended
            setRecommendedJobs(jobs.slice(0, 3));
        } catch (error) {
            console.error('Error fetching jobs:', error);
            console.error('Error details:', error.response?.data || error.message);
            setAllJobs([]);
            setRecommendedJobs([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCourses = async () => {
        try {
            setCoursesLoading(true);
            const response = await courseService.getAll();

            // API returns: { success: true, data: [courses] }
            let coursesData = [];
            if (response && response.success && response.data) {
                coursesData = Array.isArray(response.data) ? response.data : [];
            } else if (Array.isArray(response)) {
                // Fallback: if response is directly an array
                coursesData = response;
            }

            setCourses(coursesData.slice(0, 6)); // Show first 6 courses
        } catch (error) {
            console.error('Error fetching courses:', error);
            setCourses([]);
        } finally {
            setCoursesLoading(false);
        }
    };

    useEffect(() => {
        fetchJobs();
        fetchCourses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatSalary = (salary, currency) => {
        // show 18000 as 18k and make month
        // format the currency to translate it
        const currencyTranslation = {
            'USD': t('employee.home.currency.USD'),
            'EUR': t('employee.home.currency.EUR'),
            'EGP': t('employee.home.currency.EGP'),
        };

        // if the salary is not specified, return the translation for salary not specified
        if (!salary) return t('employee.home.salaryNotSpecified');

        // if the salary is greater than 1000, return the translation for salary in thousands
        if (salary >= 1000) {
            if (salary % 1000 === 0) {
                return `${Math.round(salary / 1000)}k ${currencyTranslation[currency] || currency} ${t('employee.home.perMonth')}`;
            } else {
                return `${(salary / 1000).toFixed(1)}k ${currencyTranslation[currency] || currency} ${t('employee.home.perMonth')}`;
            }
        }
    };

    const formatWorkType = (type) => {
        const types = {
            'full_time': t('employee.home.workType.fullTime'),
            'part_time': t('employee.home.workType.partTime'),
            'contract': t('employee.home.workType.contract'),
            'freelance': t('employee.home.workType.freelance')
        };
        return types[type] || type;
    };


    const formatPresenceType = (type) => {
        const types = {
            'remote': t('employee.home.presenceType.remote'),
            'hybrid': t('employee.home.presenceType.hybrid'),
            'onsite': t('employee.home.presenceType.onsite')
        };
        return types[type] || type;
    };

    const formatLocation = (location) => {
        // Location can be an object with lat/long or a string
        if (!location) return 'القاهرة'; // Default location
        if (typeof location === 'string') return location;
        if (typeof location === 'object' && (location.lat || location.long)) {
            // If it's a location object, return a default city name
            // You could also use reverse geocoding here to get the actual city name
            return 'القاهرة';
        }
        return 'القاهرة';
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate('/employee/vacancies', { state: { search: searchQuery } });
        }
    };

    const handleJobClick = (jobId) => {
        navigate(`/employee/vacancies/${jobId}`);
    };

    const handleSave = (jobId) => {
        vacancyService.save(jobId).then((response) => {
            setAllJobs(allJobs.map((job) => job.id === jobId ? { ...job, is_saved: true } : job));
        }).catch((error) => {
            console.error('Error saving vacancy:', error);
        });
    };

    const handleUnsave = (jobId) => {
        vacancyService.unsave(jobId).then((response) => {
            setAllJobs(allJobs.map((job) => job.id === jobId ? { ...job, is_saved: false } : job));
        }).catch((error) => {
            console.error('Error unsaving vacancy:', error);
            setAllJobs(allJobs.map((job) => job.id === jobId ? { ...job, is_saved: true } : job));
        });
    };

    const handleCourseClick = (courseId) => {
        navigate(`/employee/courses/${courseId}`);
    };

    const handleStartCourse = async (courseId, e) => {
        e.stopPropagation();
        try {
            const response = await courseService.startCourse(courseId);
            if (response.success) {
                // Navigate to course detail page
                navigate(`/employee/courses/${courseId}`);
            }
        } catch (error) {
            console.error('Error starting course:', error);
            alert(error.response?.data?.message || t('employee.home.courseStartFailed') || 'Failed to start course');
        }
    };

    return (
        <div className="employee-home-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Notification and Profile */}
            {/* <div className="home-header d-flex justify-content-between align-items-center px-3 py-3">
                <div className="user-profile-header d-flex align-items-center gap-2 rtl">
                    <div className="profile-picture">
                        {user?.image ? (
                            <img src={user?.image} alt="Profile" className="profile-img" />
                        ) : (
                            <div className="profile-img text-center align-content-center">
                                <b>
                                    {user?.name?.charAt(0).toUpperCase() ?? 'E'}
                                </b>
                            </div>
                        )}
                    </div>
                    <div className="user-info text-end">
                        <div className="user-name">{user?.name || ''}</div>
                        <div className="user-job-title">{user?.job_title_name || ''}</div>
                    </div>
                </div>
                <div className="notification-icon">
                    <span className="bell-icon">🔔</span>
                </div>
            </div> */}
            <EmployeeNav user={user} />

            {/* Search Bar */}
            <div className="search-section px-3 mb-3">
                <form onSubmit={handleSearch} className="search-bar-container position-relative">
                    <button type="submit" className="search-btn position-absolute">
                        <span>🔍</span>
                    </button>
                    <input
                        type="text"
                        className="search-input form-control"
                        placeholder={t('employee.home.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </form>
            </div>

            {/* Promotional Banner */}
            <div className="promotional-banner mx-3 mb-4">
                <div className="banner-content p-4">
                    <div className="banner-text">
                        <h2 className="banner-title">{t('employee.home.newJobs')}</h2>
                        <p className="banner-description">
                            {t('employee.home.newJobsDescription')}
                        </p>
                    </div>
                    <div className="banner-image">
                        <img
                            src="/images/home_banner.png"
                            alt="New Jobs"
                            className="banner-img"
                            onError={(e) => {
                                e.target.src = '';
                            }}
                        />
                    </div>
                </div>
            </div>

            {/* Courses Section */}
            <div className="courses-section px-3 mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="section-title mb-0">{t('employee.home.courses') || 'Courses'}</h3>
                    {courses.length > 0 && (
                        <button
                            className="btn-view-all"
                            onClick={() => navigate('/employee/courses')}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'rgb(91 137 203)',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                        >
                            {t('employee.home.viewAll') || 'View All'}
                        </button>
                    )}
                </div>
                {coursesLoading ? (
                    <Loader size="small" />
                ) : courses.length > 0 ? (
                    <div className="courses-list d-flex gap-3" style={{ overflowX: 'auto', paddingBottom: '10px' }}>
                        {courses.map((course) => (
                            <div
                                key={course.id}
                                className="course-card"
                                style={{
                                    minWidth: '200px',
                                    maxWidth: '200px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => handleCourseClick(course.id)}
                            >
                                <div className="course-image-container mb-2">
                                    {course.image ? (
                                        <img
                                            src={course.image}
                                            alt={course.name}
                                            className="course-image"
                                            style={{ width: '100%', height: '120px', objectFit: 'cover', borderRadius: '8px' }}
                                            onError={(e) => {
                                                e.target.style.display = 'none';
                                                e.target.nextSibling.style.display = 'flex';
                                            }}
                                        />
                                    ) : null}
                                    <div
                                        className="course-image-placeholder"
                                        style={{
                                            width: '100%',
                                            height: '120px',
                                            backgroundColor: '#6366f1',
                                            borderRadius: '8px',
                                            display: course.image ? 'none' : 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'white',
                                            fontSize: '24px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        {course.name?.charAt(0).toUpperCase() || 'C'}
                                    </div>
                                </div>
                                <div className="course-info">
                                    <h4 className="course-name mb-1" style={{ fontSize: '14px', fontWeight: '600', marginBottom: '4px' }}>
                                        {course.name}
                                    </h4>
                                    <p className="course-videos-count mb-2" style={{ fontSize: '12px', color: '#6c757d', marginBottom: '8px' }}>
                                        {course.videos_count || 0} {t('employee.home.videos') || 'videos'}
                                    </p>
                                    <button
                                        className="btn-start-course"
                                        onClick={(e) => handleStartCourse(course.id, e)}
                                        style={{
                                            width: '100%',
                                            padding: '8px',
                                            backgroundColor: 'rgb(91 137 203)',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '6px',
                                            fontSize: '12px',
                                            fontWeight: '500',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {t('employee.home.startCourse') || 'Start Course'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-4" style={{ color: '#6c757d' }}>
                        {t('employee.home.noCourses') || 'No courses available'}
                    </div>
                )}
            </div>

            {/* Recommended Jobs Section */}
            {recommendedJobs.length > 0 && (
                <div className="recommended-jobs-section px-3 mb-4">
                    <h3 className="section-title mb-3">{t('employee.home.recommendedForYou')}</h3>
                    <div className="recommended-job-card">
                        {recommendedJobs[0] && (
                            <div className="job-card-recommended d-flex align-items-center justify-content-between p-3">
                                <div className="company-logo-recommended">
                                    {recommendedJobs[0].company?.logo ? (
                                        <img
                                            src={recommendedJobs[0].company?.logo}
                                            alt="Company Logo"
                                            className="logo-img"
                                        />
                                    ) : (
                                        <div className="company-logo-text text-dark align-content-center">
                                            <b>
                                                {recommendedJobs[0].company?.name?.charAt(0).toUpperCase()}
                                            </b>
                                        </div>
                                    )}
                                </div>
                                <div className="job-info-recommended text-center flex-grow-1">
                                    <div className="job-title-recommended">
                                        {recommendedJobs[0].job_title?.name || t('employee.home.jobTitle')}
                                    </div>
                                    <div className="job-details-recommended">
                                        {formatWorkType(recommendedJobs[0].type_of_work)} - {recommendedJobs[0].company?.name || t('employee.home.company')}
                                    </div>
                                </div>
                                <button className="btn-more">{t('employee.home.more')}</button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* All Jobs Section */}
            <div className="all-jobs-section px-3 mb-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="section-title mb-0">{t('employee.home.allJobsTitle')}</h3>
                    {allJobs.length > 3 && (
                        <button
                            className="btn-view-all"
                            onClick={() => navigate('/employee/vacancies')}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: 'rgb(91 137 203)',
                                fontSize: '14px',
                                fontWeight: '500',
                                cursor: 'pointer'
                            }}
                        >
                            {t('employee.home.viewAll') || 'View All'}
                        </button>
                    )}
                </div>
                <div className="jobs-list">
                    {loading ? (
                        <Loader size="medium" />
                    ) : allJobs.length > 0 ? (
                        allJobs.slice(0, 3).map((job) => (
                            <div key={job.id} className="job-card mb-3">
                                <div className="d-flex align-items-start gap-2">
                                    <div className="company-logo-card">
                                        {job.company?.logo ? (
                                            <img
                                                src={job.company?.logo}
                                                alt="Company Logo"
                                                className="logo-img"
                                            />
                                        ) : (
                                            <div className="company-logo-text text-dark align-content-center">
                                                <b>
                                                    {job.company?.name?.charAt(0).toUpperCase()}
                                                </b>
                                            </div>
                                        )}
                                    </div>
                                    {/* add class rtl if the locale is arabic */}
                                    <div className={`job-content flex-grow-1 ${locale === 'ar' ? 'rtl' : 'ltr'}`}>
                                        <div className="job-title-card mb-2">
                                            {job.job_title?.name || t('employee.home.jobTitle')}
                                        </div>
                                        <div className="job-company-location mb-2">
                                            {job.company?.name || t('employee.home.companyName')} - {formatLocation(job.location)} - مصر
                                        </div>
                                        <div className="job-tags d-flex gap-2 mb-2 mt-3">
                                            <span className="job-tag">{formatPresenceType(job.presence_type)}</span>
                                            <span className="job-tag">{formatWorkType(job.type_of_work)}</span>

                                        </div>
                                    </div>
                                    <div className={`d-flex flex-column justify-content-between book-mark-div ${locale === 'ar' ? 'rtl' : 'ltr'}`}>

                                        <button className="bookmark-btn" onClick={() => job.is_saved ? handleUnsave(job.id) : handleSave(job.id)}>
                                            {job.is_saved ? (
                                                <span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bookmark-filled">
                                                        <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
                                                    </svg>


                                                </span>
                                            ) : (
                                                <span>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-bookmark"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" /></svg>
                                                </span>
                                            )}
                                        </button>
                                        <div className='salary-tag-container'>

                                            <span className='salary-tag mb-3 d-block'>
                                                {formatSalary(job.salary, job.currency)}
                                            </span>
                                            {/* check if the vacancy is open */}
                                            {job.is_open && (

                                                <>
                                                {job.is_applied ? (
                                                    <button className="btn-apply btn-applied"
                                                    onClick={() => handleJobClick(job.id)}

                                                    >
                                                        {t('employee.home.applied')}

                                                    </button>
                                                ) : (
                                                    <button
                                                        className="btn-apply"
                                                        onClick={() => handleJobClick(job.id)}
                                                    >
                                                        {t('employee.home.apply')}
                                                    </button>
                                                )}
                                                </>
                                            )}
                                            {/* check if the vacancy is closed */}
                                            {job.is_closed && (
                                                <button className="btn-apply btn-closed disabled">
                                                    {t('employee.home.closed')}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-4">{t('employee.home.noJobs')}</div>
                    )}
                </div>
            </div>

            {/* Bottom Navigation Bar */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default EmployeeHome;

